import * as React from 'react';
import { ICoreContext } from '@msdyn365-commerce/core';
import { Node } from '@msdyn365-commerce-modules/utilities';
import { IBusinessSignUpItem } from '@msdyn365-commerce-modules/business-account-management';
import { IBusinessSignUpResources } from '../../definition-extensions/business-sign-up.ext.props.autogenerated';
import { debounce } from 'lodash';
import { getEntityAsync } from '../../../../actions/get-entity';

interface IBusinessSignUpItemDepartmentProps extends IBusinessSignUpItem {
    context: ICoreContext;
    inputId: string;
    resources: IBusinessSignUpResources;
}

function setValueAndTriggerEvent(selector: string, value: string) {
    const element = document.querySelector(selector) as HTMLInputElement | null;

    if (!element) {
        return;
    }

    const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
        window.HTMLInputElement.prototype,
        'value')?.set;
    nativeInputValueSetter?.call(element, value);
    const event = new CustomEvent('change', { detail: { preventAutoCompleteSearch: true }, bubbles: true });
    element.dispatchEvent(event);
};

const BusinessSignUpItemDepartment: React.FC<IBusinessSignUpItemDepartmentProps> = ({
    context,
    errorMessage,
    input: inputOriginal,
    label,
    resources,
    wrapper: wrapperOriginal
}) => {
    const [isSearching, setIsSearching] = React.useState(false);

    const doDepartmentSearch = React.useCallback(
        debounce(async (orgNumber: string) => {
            if (!orgNumber) {
                return;
            }

            setIsSearching(true);

            // Remove any non-digit
            orgNumber = orgNumber.replace(/\D/g, '').slice(-9);

            // Skip searching if the org number is not 9 digits
            if (orgNumber.length !== 9) {
                await new Promise(resolve => setTimeout(resolve, 200));
                setIsSearching(false);
                return;
            }

            try {
                const result = await getEntityAsync({ callerContext: context.actionContext }, orgNumber);

                setValueAndTriggerEvent('#CompanyName', result.Name || '');
                setValueAndTriggerEvent('#company_addressstreet', result.BusinessAddresses?.Addresses?.[0] || '');
                setValueAndTriggerEvent('#company_addresszipcode', result.BusinessAddresses?.ZipCode || '');
                setValueAndTriggerEvent('#company_addresscity', result.BusinessAddresses?.City || '');
            } catch {
                // Nothing found
            } finally {
                setIsSearching(false);
            }
        }, 1000),
        []
    );

    // Get the original onChange event and store it in props in case we need to call it
    const onChangeOriginal: (e: React.ChangeEvent<HTMLInputElement>) => void = (inputOriginal as JSX.Element).props.onChange;

    // Replace the onChange event with our own so we can do the department search when the user types
    const input = React.cloneElement(inputOriginal as React.ReactElement, {
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
            doDepartmentSearch(e.target.value);
            onChangeOriginal(e);
        },
        onChangeOriginal
    });
    // Replace the wrapper class to make the input field smaller
    const wrapper = { ...wrapperOriginal, className: wrapperOriginal.className.replace('width-50', 'width-25') };

    // Hide label and error message if rendering address section
    return (
        <Node {...wrapper}>
            {React.cloneElement(label as React.ReactElement, { text: resources.organizationIdLabel })}
            {errorMessage}
            <div>
                {isSearching &&
                    <span className={`msc-address-form__input-spinner`} />
                }
                {!isSearching &&
                    <button className='msc-address-form__input-search-btn' tabIndex={-1} />
                }
                {input}
            </div>
        </Node>
    );
};

export default BusinessSignUpItemDepartment;
